<script setup lang="ts">
import { onMounted } from 'vue';
import { useCountStore } from '@/stores/counts.js';
import { useJobStore } from '@/stores/jobs.js';
import { storeToRefs } from 'pinia';

const countStore = useCountStore();
const { loadJobs } = useJobStore();
const { showCurrentQueue } = storeToRefs(useJobStore());
onMounted(() => {
  countStore.loadJobCounts();
});

function showJobs(queue: string, status: string) {
  loadJobs(queue, status);
  showCurrentQueue.value = true;
}
</script>

<template>
  <div>
    <div class="flex justify-end">
      <button
        class="text-gray-500 cursor-pointer disabled:text-gray-200 disabled:animate-spin"
        :disabled="countStore.isLoading"
        @click="countStore.loadJobCounts"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="size-6"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
          />
        </svg>
      </button>
    </div>
    <!-- Grid -->
    <div class="grid sm:grid-rows-1 gap-4 sm:gap-6">
      <!-- Card -->
      <template v-for="(counts, key) in countStore.sortedCounts">
        <div class="flex flex-col bg-slate-50 border shadow-sm rounded-xl">
          <p class="px-4 pt-4 text-lg uppercase tracking-wide text-gray-500">{{ key }}</p>
          <!-- Card Section -->
          <div class="p-4">
            <!-- Grid -->
            <div class="grid sm:grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              <!-- Card -->
              <div class="flex flex-col bg-white border shadow-sm rounded-xl">
                <div class="p-4 md:p-5 flex justify-between gap-x-3">
                  <div>
                    <p class="text-xs uppercase tracking-wide text-gray-500">Pending</p>
                    <div class="mt-1 flex items-center gap-x-2">
                      <h3 class="text-xl sm:text-2xl font-medium text-gray-800">
                        {{ counts.pending }}
                      </h3>
                    </div>
                  </div>
                </div>

                <button
                  class="py-3 px-4 md:px-5 inline-flex justify-between items-center text-sm text-gray-600 border-t border-gray-200 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 rounded-b-xl"
                  @click="showJobs(key, 'pending')"
                >
                  View jobs
                  <svg
                    class="shrink-0 size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
              <!-- End Card -->

              <!-- Card -->
              <div class="flex flex-col bg-white border shadow-sm rounded-xl">
                <div class="p-4 md:p-5 flex justify-between gap-x-3">
                  <div>
                    <p class="text-xs uppercase tracking-wide text-gray-500">Reserved</p>
                    <div class="mt-1 flex items-center gap-x-2">
                      <h3 class="text-xl sm:text-2xl font-medium text-gray-800">
                        {{ counts.reserved }}
                      </h3>
                    </div>
                  </div>
                </div>

                <button
                  class="py-3 px-4 md:px-5 inline-flex justify-between items-center text-sm text-gray-600 border-t border-gray-200 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 rounded-b-xl"
                  @click="showJobs(key, 'reserved')"
                >
                  View jobs
                  <svg
                    class="shrink-0 size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
              <!-- End Card -->

              <!-- Card -->
              <div class="flex flex-col bg-white border shadow-sm rounded-xl">
                <div class="p-4 md:p-5 flex justify-between gap-x-3">
                  <div>
                    <p class="text-xs uppercase tracking-wide text-gray-500">Delayed</p>
                    <div class="mt-1 flex items-center gap-x-2">
                      <h3 class="text-xl sm:text-2xl font-medium text-gray-800">
                        {{ counts.delayed }}
                      </h3>
                    </div>
                  </div>
                </div>

                <button
                  class="py-3 px-4 md:px-5 inline-flex justify-between items-center text-sm text-gray-600 border-t border-gray-200 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 rounded-b-xl"
                  @click="showJobs(key, 'delayed')"
                >
                  View jobs
                  <svg
                    class="shrink-0 size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </div>
              <!-- End Card -->
            </div>
            <!-- End Grid -->
          </div>
          <!-- End Card Section -->
        </div>
      </template>
      <!-- End Card -->
    </div>
    <!-- End Grid -->
  </div>
</template>
